import React, { useEffect, useRef, useState } from 'react'
import Step from '../../components/Step'
import ButtonSelect from '../../elements/ButtonSelect'
import Resi from '../Resi'
import { fieldName, enforce } from '../HelpersResi'
import Input from '../../elements/Input'
import Select from '../../elements/Select'
import ButtonContainer from '../../elements/ButtonContainer'
import Button from '../../elements/Button'
import ScrollWhenAppears from '../../elements/ScrollWhenAppears'
import {
    AMOUNT_INVALID,
    CLAIM_ENTRY_REQUIRED,
    CLAIM_ROWS_REQUIRED,
    YES_NO_OPTIONS,
    ClaimHistoryDetailApi,
    claimExcessTypeOptions,
    claimMonth,
    claimMonthOptions,
    claimStatusOptions,
    claimAmount,
    claimTotals,
    claimTypeLabel,
    claimYear,
    claimYearOptions,
    formatDollar,
    parseAmount,
    toClaimHistoryDetail,
} from '../simplifiedQuoting'


const numberOfClaims = fieldName('numberOfClaims')
const valueOfClaims = fieldName('valueOfClaims')
const additionalComments = fieldName('additionalComments')
const claimsHistoryDetailApi = fieldName('claimsHistoryDetailApi')
const hasClaims = fieldName('__hasClaims__')

export const rules = {
    [numberOfClaims]: [
        enforce.required('Required'),
    ],
    [valueOfClaims]: [
        enforce.required('Required'),
    ],
    [additionalComments]: [],
    [claimsHistoryDetailApi]: [
        enforce.hasAtLeastOneEntry(CLAIM_ROWS_REQUIRED),
    ],
    [hasClaims]: [
        enforce.isTrueOrFalse('Required'),
    ],
}


export default () => {
    const navigation = Resi.useNavigation()
    const fields = Resi.useFields()
    const options = Resi.useOptions()
    const errors = Resi.useErrors()

    const typeOptions = claimExcessTypeOptions(options.data?.product?.DOD_CLAIM_EXCESS_TYPE)
    const rows = (fields.value(claimsHistoryDetailApi) || []) as ClaimHistoryDetailApi[]
    const storedAnswer = fields.value(hasClaims)
    const selected = storedAnswer === true || storedAnswer === false
        ? storedAnswer
        : Number(fields.value(numberOfClaims)) > 0
            ? true
            : fields.value(numberOfClaims) === 0 || fields.value(numberOfClaims) === '0'
                ? false
                : null

    const [entryYear, setEntryYear] = useState('')
    const [entryMonth, setEntryMonth] = useState('')
    const [entryStatus, setEntryStatus] = useState('Closed')
    const [entryType, setEntryType] = useState('')
    const [entryDetails, setEntryDetails] = useState('')
    const [entryAmount, setEntryAmount] = useState<number | ''>('')
    const [entryError, setEntryError] = useState('')

    const rowError = errors.of(claimsHistoryDetailApi)
    const rowErrorRef = useRef<HTMLDivElement>(null)
    const scroll = Resi.useScroll()
    const totals = claimTotals(rows)

    useEffect(() => {
        if (rowError && rowError.length > 0 && rowErrorRef.current) {
            scroll.ifNoneAbove(rowErrorRef, 26)
        }
    }, [rowError])

    function syncClaims(nextRows: ClaimHistoryDetailApi[], answered: boolean) {
        const nextTotals = claimTotals(nextRows)
        fields.setValues({
            __hasClaims__: answered,
            claimsHistoryDetailApi: nextRows,
            numberOfClaims: nextTotals.numberOfClaims,
            valueOfClaims: nextTotals.valueOfClaims,
        })
    }

    function onSelected(value: boolean) {
        if (value === false) {
            syncClaims([], false)
            errors.setErrorsForField(claimsHistoryDetailApi, [])
            navigation.goToNext()
            return
        }
        fields.setValue(hasClaims)(true)
    }

    function onAdd() {
        setEntryError('')
        const amount = parseAmount(entryAmount)
        if (!entryYear || !entryMonth || !entryType || Number.isNaN(amount)) {
            setEntryError(CLAIM_ENTRY_REQUIRED)
            return
        }
        if (amount <= 0) {
            setEntryError(AMOUNT_INVALID)
            return
        }

        const detail = toClaimHistoryDetail({
            year: entryYear,
            month: entryMonth,
            status: entryStatus || 'Closed',
            code: entryType,
            details: entryDetails,
            amount,
        })
        if (!detail) {
            setEntryError(CLAIM_ENTRY_REQUIRED)
            return
        }

        syncClaims([...rows, detail], true)
        errors.setErrorsForField(claimsHistoryDetailApi, [])
        setEntryYear('')
        setEntryMonth('')
        setEntryStatus('Closed')
        setEntryType('')
        setEntryDetails('')
        setEntryAmount('')
    }

    function onRemove(index: number) {
        syncClaims(rows.filter((_, rowIndex) => rowIndex !== index), true)
    }

    function onContinue() {
        const current = (fields.value(claimsHistoryDetailApi) || []) as ClaimHistoryDetailApi[]
        if (current.length === 0) {
            errors.setErrorsForField(claimsHistoryDetailApi, [CLAIM_ROWS_REQUIRED])
            return
        }
        syncClaims(current, true)
        errors.setErrorsForField(claimsHistoryDetailApi, [])
        navigation.goToNext()
    }

    return (
        <Step>
            <h2>Have you had any claims in the past three (3) years?</h2>
            <ButtonSelect
                options={YES_NO_OPTIONS}
                selected={selected}
                colsLg={3}
                colsSm={1}
                callback={onSelected}
                sort={false}
            />
            <ScrollWhenAppears condition={selected === true}>
                <div className="dod-history">
                    <h4>Please provide details</h4>
                    <div className="dod-entry-width dod-entry-pair">
                        <Select label="Year" field={[entryYear, setEntryYear]} options={claimYearOptions(fields.value('startDate'))} />
                        <Select label="Month" field={[entryMonth, setEntryMonth]} options={claimMonthOptions()} />
                    </div>
                    <div className="dod-entry-width dod-entry-pair">
                        <Select label="Claim Status" field={[entryStatus, setEntryStatus]} options={claimStatusOptions()} />
                        <Select
                            label="Type of Loss"
                            field={[entryType, setEntryType]}
                            options={typeOptions}
                        />
                    </div>
                    <div className="dod-entry-width">
                        <Input
                            field={[entryDetails, (value: string) => setEntryDetails(value.replace(/[0-9]/g, '').slice(0, 255))]}
                            type="text"
                            label="Additional Details"
                            maxLength={255}
                        />
                    </div>
                    <div className="dod-entry-width">
                        <Input
                            field={[entryAmount, setEntryAmount]}
                            type="dollar"
                            label="Amount"
                        />
                    </div>
                    {entryError && <div className="dod-history-error">{entryError}</div>}
                    <ButtonContainer>
                        <Button label="Add" type="outline-secondary" callback={onAdd} />
                    </ButtonContainer>

                    {rows.length > 0 &&
                        <div className="dod-history-list dod-entry-width">
                            {rows.map((row, index) => (
                                <div className="dod-history-entry" key={`${row.lossDate}-${row.claimType}-${index}`}>
                                    <div className="dod-history-fields">
                                        <div className="main-label">Year</div>
                                        <div>{claimYear(row)}</div>
                                        <div className="main-label">Month</div>
                                        <div>{claimMonth(row)}</div>
                                        <div className="main-label">Claim Status</div>
                                        <div>{row.claimStatus}</div>
                                        <div className="main-label">Type of Loss</div>
                                        <div>{claimTypeLabel(row.claimType, typeOptions)}</div>
                                        <div className="main-label">Additional Details</div>
                                        <div>{row.claimDescription}</div>
                                        <div className="main-label">Amount</div>
                                        <div>{formatDollar(claimAmount(row))}</div>
                                    </div>
                                    <button type="button" className="btn btn-outline-secondary dod-remove" aria-label="Remove" onClick={() => onRemove(index)}>
                                        <i className="fas fa-trash" aria-hidden="true" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    }

                    <div className="dod-history-totals dod-entry-width">
                        <div>
                            <div className="main-label">Number of Claims</div>
                            <div>{totals.numberOfClaims}</div>
                        </div>
                        <div>
                            <div className="main-label">Claims Amount</div>
                            <div>{formatDollar(totals.valueOfClaims)}</div>
                        </div>
                    </div>

                    {rowError && rowError.length > 0 && <div className="dod-history-error" ref={rowErrorRef}>{rowError[0]}</div>}
                    <ButtonContainer>
                        <Button label="Continue" type="primary" icon="fas fa-chevron-right" callback={onContinue} />
                    </ButtonContainer>
                </div>
            </ScrollWhenAppears>
        </Step>
    )
};